
import React, { useState, useMemo, useEffect } from 'react';
import { useDataQuery, useDataMutation, useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from '../../auth/AuthContext';
import {
  Button,
  InputField,
  SingleSelect,
  SingleSelectOption,
  CircularLoader,
  AlertBar,
  IconAdd16,
  IconFullscreenExit16,
  FileInput
} from '@dhis2/ui';
import { useOrgUnits } from '../hooks/useOrgUnits';
import { useNavigate } from 'react-router-dom';
import { assignUserToSpecificOrgs } from '../../../utils/api';
import DepartmentManager from './DepartmentManager';

// Constants
const ATTRIBUTE_IDS = {
  ORG_EMAIL: 'P1KhiQpaXO4',
  ORG_TYPE: 'WxaXz5yCf4f',
  FINANCIAL_START: 'jYywVrwX55m',   
  FINANCIAL_END: 'Amtp5VXvduY'      
};

// Save metadata to program event (for FY dates, org type, etc.)
const COMPANY_METADATA_PROGRAM = {
  ID: 'NkReudUAW4F',
  STAGE_ID: 'htIWgNQWEMm',
  DATA_ELEMENTS: {
    EMAIL: 'NkCxPqLeDpq',
    ORG_TYPE: 'QIMf5a1r1Ot',
    FIN_START: 'U55MqwBcRAe',
    FIN_END: 'kgdk4NXu4f5',
    COMPANY_LOGO: 'muysgcGfkyN',
    KYC_FILES: 'mnXa4HPVBgM'
  }
};


const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

const normalizeFinancialBoundary = (value) => {
  if (!value) return '';
  const trimmedValue = String(value).trim();
  const monthDayMatch = trimmedValue.match(/^(\d{2})-(\d{2})$/);
  if (monthDayMatch) {
    return `${monthDayMatch[1]}-${monthDayMatch[2]}`;
  }
  const fullDateMatch = trimmedValue.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (fullDateMatch) {
    return `${fullDateMatch[1]}-${fullDateMatch[2]}`;
  }
  return '';
};


const FINANCIAL_REFERENCE_YEAR = 2000;

const buildFinancialDatesForStorage = (startValue, endValue) => {
  const normalizedStart = normalizeFinancialBoundary(startValue);
  const normalizedEnd = normalizeFinancialBoundary(endValue);
  const start = parseFinancialBoundary(normalizedStart);
  let end = parseFinancialBoundary(normalizedEnd);

  if (!start || !end) {
    return { start: '', end: '' };
  }

  let endYear = FINANCIAL_REFERENCE_YEAR;
  if (end <= start) {
    end = parseFinancialBoundary(normalizedEnd, 1);
    endYear = FINANCIAL_REFERENCE_YEAR + 1;
  }

  if (!end) {
    return { start: '', end: '' };
  }

  return {
    start: `${FINANCIAL_REFERENCE_YEAR}-${normalizedStart}`,
    end: `${endYear}-${normalizedEnd}`
  };
};

const parseFinancialBoundary = (value, rolloverYear = 0) => {
  const normalizedValue = normalizeFinancialBoundary(value);
  if (!normalizedValue) return null;

  const [month, day] = normalizedValue.split('-').map(Number);
  if (!month || !day) return null;

  const year = FINANCIAL_REFERENCE_YEAR + rolloverYear;
  
  // Create date using UTC
  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  
  // Validate the date is correct
  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return null;
  }
  
  return parsedDate;
};

const buildFinancialBoundary = (month, day) => {
  if (!month && !day) return '';
  if (!month || !day) return `${month || ''}-${day || ''}`.replace(/^-|-$/g/ '');
  const dayNumber = Number(day);
  return `${month}-${String(dayNumber).padStart(2, '0')}`;
};

// Convert month-day format to full date for DHIS2 attributes (using current year)
const monthDayToDateString = (month, day) => {
  if (!month || !day) return null;
  const year = new Date().getFullYear();
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const date = new Date(year, monthNum - 1, dayNum);
  return date.toISOString().split('T')[0];
};

const useFinancialDateValidation = (startDate, endDate) => {
  const [errors, setErrors] = useState({});
  useEffect(() => {
    const newErrors = {};
    if (startDate && endDate) {
      const normalizedStart = normalizeFinancialBoundary(startDate);
      const normalizedEnd = normalizeFinancialBoundary(endDate);
      const start = parseFinancialBoundary(normalizedStart);
      let end = parseFinancialBoundary(normalizedEnd);
      if (!start || !end) {
        newErrors.dateRange = 'Enter a valid month and day for both dates';
      } else {
        if (end <= start) {
          end = parseFinancialBoundary(normalizedEnd, 1);
        }
        if (!end || end <= start) {
          newErrors.dateRange = 'End date must come after start date in the annual cycle';
        }
        const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
        if (diffDays < 330 || diffDays > 370) {
          newErrors.duration = 'Financial year should span about 12 months';
        }
      }
    }
    setErrors(newErrors);
  }, [startDate, endDate]);
  return errors;
};

const CURRENT_USER_QUERY = {
  me: {
    resource: 'me',
    params: { fields: 'id,username,organisationUnits[id,name]' }
  }
};

// Helper for generating code
function generateOrgCode(name) {
  const initials = name.trim()
    .split(/\s+/)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .padEnd(3, 'X')
    .slice(0, 3);
  return `OU_${initials}_${Date.now().toString().slice(-4)}`;
}

// Helper for direct API calls
async function createOrgUnitDirect(payload) {
  const response = await fetch('/api/organisationUnits', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to create org unit (${response.status}): ${errorText}`);
  }
  
  return response.json();
}

// Add this helper function before handleSubmit
const ensureMetadataEventExists = async (engine, orgUnitId, enrollmentId, trackedEntityInstanceId) => {
  try {
    // Check if event exists
    const eventsResponse = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: COMPANY_METADATA_PROGRAM.ID,
          programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
          enrollment: enrollmentId,
          fields: 'event',
          pageSize: 1
        }
      }
    });
    
    const existingEvent = eventsResponse.events?.events?.[0];
    if (existingEvent) {
      console.log('✅ Metadata event already exists:', existingEvent.event);
      return existingEvent.event;
    }
    
    // Create empty event
    console.log('📝 Creating empty metadata event...');
    const today = new Date().toISOString().split('T')[0];
    
    const createResponse = await fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        program: COMPANY_METADATA_PROGRAM.ID,
        programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
        orgUnit: orgUnitId,
        eventDate: today,
        status: 'COMPLETED',
        trackedEntityInstance: trackedEntityInstanceId,
        enrollment: enrollmentId,
        dataValues: []
      })
    });
    
    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      console.warn('Could not create empty event:', errorText);
      return null;
    }
    
    const result = await createResponse.json();
    const newEventId = result?.response?.uid || result?.uid;
    console.log('✅ Empty metadata event created:', newEventId);
    return newEventId;
    
  } catch (error) {
    console.error('Failed to ensure metadata event:', error);
    return null;
  }
};

async function fetchOrgUnitIdByName(engine, name, parentId) {
  try {
    const response = await engine.query({
      orgUnits: {
        resource: 'organisationUnits',
        params: {
          fields: 'id',
          filter: [`name:eq:${name.trim()}`, `parent.id:eq:${parentId}`],
          pageSize: 1
        }
      }
    });

    return response?.orgUnits?.organisationUnits?.[0]?.id || null;
  } catch (error) {
    console.error('Error fetching org unit by name:', error);
    return null;
  }
}

async function createOrgUnitWithDuplicateHandling(engine, payload) {
  const normalizedName = payload.name.trim();
  const parentId = payload.parent?.id;

  if (!parentId) {
    throw new Error(`Cannot create org unit "${normalizedName}" without a parent ID`);
  }

  const existingId = await fetchOrgUnitIdByName(engine, normalizedName, parentId);
  if (existingId) {
    console.log(`Org unit "${normalizedName}" already exists with ID: ${existingId}, skipping creation`);
    return { id: existingId, existing: true };
  }

  try {
    return await createOrgUnitDirect({
      ...payload,
      name: normalizedName,
      shortName: (payload.shortName || normalizedName).trim().substring(0, 50)
    });
  } catch (error) {
    if (error.message?.includes('(409)')) {
      console.log(`409 Conflict for "${normalizedName}", attempting to fetch existing org unit`);
      const conflictExistingId = await fetchOrgUnitIdByName(engine, normalizedName, parentId);
      if (conflictExistingId) {
        return { id: conflictExistingId, existing: true };
      }
    }

    throw error;
  }
}


// Save metadata using app-runtime (same pattern as OrganisationUpdate.jsx)
async function saveCompanyMetadataDirect(engine, orgUnitId, metadata) {
  const dataValues = [
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.EMAIL, value: (metadata.email || '').trim() },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.ORG_TYPE, value: (metadata.orgType || '').trim() },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.FIN_START, value: metadata.finStart || '' },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.FIN_END, value: metadata.finEnd || '' },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.KYC_FILES, value: metadata.kycFiles || '' } 
  ].filter(dv => dv.dataElement && (dv.value || dv.value === ''));

  const today = new Date().toISOString().split('T')[0];

  console.log('=== DEBUG: Starting saveCompanyMetadataDirect ===');
  console.log('orgUnitId:', orgUnitId);
  console.log('metadata:', metadata);

  // Get program to find tracked entity type
  const programResponse = await engine.query({
    program: {
      resource: `programs/${COMPANY_METADATA_PROGRAM.ID}`,
      params: { fields: 'id,trackedEntityType[id]' }
    }
  });

  console.log('Program response:', JSON.stringify(programResponse, null, 2));

  const trackedEntityTypeId = programResponse.program?.trackedEntityType?.id;
  console.log('trackedEntityTypeId:', trackedEntityTypeId);
  
  if (!trackedEntityTypeId) {
    throw new Error('Company metadata program is missing a tracked entity type');
  }

  // Find or create TEI
  let teiId = null;
  
  console.log('Searching for existing TEI...');
  const existingTeisResponse = await engine.query({
    trackedEntityInstances: {
      resource: 'trackedEntityInstances',
      params: {
        trackedEntityType: trackedEntityTypeId,
        ou: orgUnitId,
        ouMode: 'SELECTED',
        fields: 'trackedEntityInstance',
        pageSize: 1
      }
    }
  });

  console.log('Existing TEIs response:', JSON.stringify(existingTeisResponse, null, 2));

  teiId = existingTeisResponse.trackedEntityInstances?.trackedEntityInstances?.[0]?.trackedEntityInstance;
  console.log('Found TEI ID:', teiId);

  if (!teiId) {
    console.log('Creating new TEI...');
    console.log('Payload:', {
      trackedEntityType: trackedEntityTypeId,
      orgUnit: orgUnitId,
      attributes: []
    });
    
    const createTeiResponse = await fetch('/api/trackedEntityInstances', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        trackedEntityType: trackedEntityTypeId,
        orgUnit: orgUnitId,
        attributes: []
      })
    });
    
    console.log('TEI creation response status:', createTeiResponse.status);
    const teiResult = await createTeiResponse.json();
    console.log('TEI creation full response:', JSON.stringify(teiResult, null, 2));
    
    teiId = teiResult?.response?.uid || teiResult?.uid;
    console.log('Extracted TEI ID:', teiId);
  }

  if (!teiId) {
    console.error('TEI creation failed - no ID extracted');
    throw new Error('Cannot create metadata event: No valid tracked entity instance');
  }

  // Continue with enrollment and event creation...
  console.log('=== TEI created/found successfully, continuing... ===');
  
  // Find or create enrollment
  let enrollmentId = null;
  
  try {
    await fetch(`/api/programs/${COMPANY_METADATA_PROGRAM.ID}/organisationUnits/${orgUnitId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
  } catch (e) {
    console.warn('Could not assign program to org unit:', e);
  }
  
  await new Promise(resolve => setTimeout(resolve, 500));

  try {
    const enrollmentsResponse = await engine.query({
      enrollments: {
        resource: 'enrollments',
        params: {
          trackedEntityInstance: teiId,
          program: COMPANY_METADATA_PROGRAM.ID,
          ou: orgUnitId,
          fields: 'enrollment',
          pageSize: 1
        }
      }
    });
    enrollmentId = enrollmentsResponse.enrollments?.enrollments?.[0]?.enrollment;
    console.log('Found enrollment ID:', enrollmentId);
  } catch (e) {
    console.warn('Could not fetch enrollment:', e);
  }

  if (!enrollmentId) {
    console.log('Creating new enrollment...');
    const createEnrollmentResponse = await fetch('/api/enrollments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        trackedEntityInstance: teiId,
        program: COMPANY_METADATA_PROGRAM.ID,
        orgUnit: orgUnitId,
        enrollmentDate: today,
        incidentDate: today,
        status: 'ACTIVE'
      })
    });
    const enrollmentResult = await createEnrollmentResponse.json();
    console.log('Enrollment creation response:', JSON.stringify(enrollmentResult, null, 2));
    enrollmentId = enrollmentResult?.enrollment || enrollmentResult?.response?.uid;
    console.log('Created enrollment ID:', enrollmentId);
  }

  if (!enrollmentId) {
    throw new Error('Could not create or find enrollment');
  }

  await new Promise(resolve => setTimeout(resolve, 500));

  // Ensure empty event exists
  await ensureMetadataEventExists(engine, orgUnitId, enrollmentId, teiId);

  // Build event payload
  const baseEventData = {
    program: COMPANY_METADATA_PROGRAM.ID,
    programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
    orgUnit: orgUnitId,
    eventDate: today,
    status: 'COMPLETED',
    trackedEntityInstance: teiId,
    enrollment: enrollmentId,
    dataValues: dataValues
  };

  // Check for existing event
  let existingEventId = null;
  try {
    const eventsResponse = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: COMPANY_METADATA_PROGRAM.ID,
          programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
          enrollment: enrollmentId,
          fields: 'event',
          pageSize: 1
        }
      }
    });
    existingEventId = eventsResponse.events?.events?.[0]?.event;
    console.log('Existing event ID:', existingEventId);
  } catch (e) {
    console.warn('Could not query existing event:', e);
  }

  if (existingEventId) {
    console.log('Updating existing event:', existingEventId);
    const updateResponse = await fetch(`/api/events/${existingEventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...baseEventData,
        event: existingEventId
      })
    });
    
    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      throw new Error(`Update failed (${updateResponse.status}): ${errorText}`);
    }
    console.log('Event updated successfully');
  } else {
    console.log('Creating new event...');
    const createResponse = await fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(baseEventData)
    });
    
    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      throw new Error(`Create failed (${createResponse.status}): ${errorText}`);
    }
    console.log('Event created successfully');
  }
}

async function deleteOrgUnitDirect(id) {
  const response = await fetch(`/api/organisationUnits/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  });
  
  if (!response.ok && response.status !== 404) {
    const errorText = await response.text();
    throw new Error(`Failed to delete org unit (${response.status}): ${errorText}`);
  }
}

// Mutations
const MUTATIONS = {
  DELETE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'delete',
    id: ({ id }) => id
  }
};

const OrgUnitForm = () => {
  const navigate = useNavigate();
  const { completeRegistration } = useAuth();
  const engine = useDataEngine();
  const [showForm, setShowForm] = useState(false);
  const [notification, setNotification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdOrgUnitId, setCreatedOrgUnitId] = useState(null);
  const [formData, setFormData] = useState({
    continentId: '',
    countryId: '',
    regionId: '',
    organisationName: '',
    organisationType: '',
    orgEmail: '',
    departments: [],
    newDepartment: ''
  });

  const [finStartMonth, setFinStartMonth] = useState('01');
  const [finStartDay, setFinStartDay] = useState('01');
  const [finEndMonth, setFinEndMonth] = useState('12');
  const [finEndDay, setFinEndDay] = useState('31');

  const [kycFiles, setKycFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const computedFinancialYearStart = buildFinancialBoundary(finStartMonth, finStartDay);
  const computedFinancialYearEnd = buildFinancialBoundary(finEndMonth, finEndDay);

  const financialDateErrors = useFinancialDateValidation(computedFinancialYearStart, computedFinancialYearEnd);

  const { data: currentUserData, loading: userLoading } = useDataQuery(CURRENT_USER_QUERY);
  const { getOrgUnitsByLevel, getOrgUnitsByParent, loading: orgUnitsLoading } = useOrgUnits();

  const continent = useMemo(() => getOrgUnitsByLevel(2), [getOrgUnitsByLevel]);
  const countries = useMemo(() => getOrgUnitsByLevel(3), [getOrgUnitsByLevel]);
  const regions = useMemo(() => formData.countryId ? getOrgUnitsByParent(formData.countryId) : [], [formData.countryId, getOrgUnitsByParent]);

  const TOP_LEVEL_ORG_ID = 'YbzM5Sy37YS';
  const userHasRealOrg = (user) => Array.isArray(user.organisationUnits) && user.organisationUnits.some(ou => ou.id !== TOP_LEVEL_ORG_ID);

  const normalizeOrgUnitName = (value) => value.trim().replace(/\s+/g, ' ').toLowerCase();

  const hasDuplicateSiblingName = (items, candidateName, currentId = null) => {
    const normalizedCandidate = normalizeOrgUnitName(candidateName);
    if (!normalizedCandidate) {
      return false;
    }

    return items.some((item) => {
      if (currentId !== null && item.id === currentId) {
        return false;
      }

      return normalizeOrgUnitName(item.name || '') === normalizedCandidate;
    });
  };

  if (userLoading) return <CircularLoader />;
  if (currentUserData?.me && userHasRealOrg(currentUserData.me)) return null;


  // Add Department
  const addDepartment = () => {
    const trimmedName = formData.newDepartment.trim();
    if (trimmedName) {
      if (hasDuplicateSiblingName(formData.departments, trimmedName)) {
        alert(`A department named "${trimmedName}" already exists under this organisation.`);
        return;
      }

      setFormData(prev => ({
        ...prev,
        departments: [...prev.departments, { id: Date.now(), name: trimmedName, newSection: '', sections: [] }],
        newDepartment: ''
      }));
    }
  };

  async function uploadKycFiles(files) {
    const uploadedIds = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/api/fileResources', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to upload ${file.name}: ${error}`);
      }
      const result = await response.json();
      const fileResourceId = result.response?.uid || result.uid;
      if (!fileResourceId) throw new Error(`No file resource ID for ${file.name}`);
      uploadedIds.push(fileResourceId);
    }
    return uploadedIds;
  }

  const ensureMetadataProgramHasTrackedEntityType = async () => {
    try {
      const programResponse = await engine.query({
        program: {
          resource: `programs/${COMPANY_METADATA_PROGRAM.ID}`,
          params: { fields: 'id,name,shortName,programType,trackedEntityType[id]' }
        }
      });
      
      const program = programResponse.program;
      const trackedEntityTypeId = program?.trackedEntityType?.id;
      
      // If already has tracked entity type, we're good
      if (trackedEntityTypeId) {
        console.log('✅ Metadata program already has tracked entity type:', trackedEntityTypeId);
        return true;
      }
      
      // ✅ FIX: Use Organization Registry Tracker (RwXRVqlEw3l) NOT Position (UGGo0Ebjl0E)
      console.log('⚠️ Metadata program missing tracked entity type, assigning Organization Registry Tracker...');
      const updateResponse = await fetch(`/api/programs/${COMPANY_METADATA_PROGRAM.ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          id: program.id,
          name: program.name,
          shortName: program.shortName,
          programType: program.programType,
          trackedEntityType: { id: 'RwXRVqlEw3l' }  // ✅ Changed from UGGo0Ebjl0E
        })
      });
      
      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        console.error('Failed to assign tracked entity type:', errorText);
        return false;
      }
      
      console.log('✅ Successfully assigned tracked entity type to metadata program');
      return true;
      
    } catch (error) {
      console.error('Failed to ensure tracked entity type:', error);
      return false;
    }
  };

  const ensureMetadataProgramHasProgramStage = async () => {
      try {
        const programResponse = await engine.query({
          program: {
            resource: `programs/${COMPANY_METADATA_PROGRAM.ID}`,
            params: { fields: 'id,name,shortName,programType,programStages[id],trackedEntityType[id]' }
          }
        });
        
        const program = programResponse.program;
        const hasMetadataStage = program.programStages?.some(
          stage => stage.id === COMPANY_METADATA_PROGRAM.STAGE_ID
        );
        
        if (hasMetadataStage) {
          console.log('✅ Metadata program already has the required program stage');
          return true;
        }
        
        console.log('⚠️ Metadata program missing program stage, adding...');
        
        const existingStages = program.programStages?.map(s => ({ id: s.id })) || [];
        
        const updateResponse = await fetch(`/api/programs/${COMPANY_METADATA_PROGRAM.ID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id: program.id,
            name: program.name,
            shortName: program.shortName,
            programType: program.programType,
            trackedEntityType: { id: program.trackedEntityType?.id || 'RwXRVqlEw3l' }, // ✅ Use correct TEI
            programStages: [
              ...existingStages,
              { id: COMPANY_METADATA_PROGRAM.STAGE_ID }
            ]
          })
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('Failed to add program stage:', errorText);
          return false;
        }
        
        console.log('✅ Successfully added program stage to metadata program');
        return true;
        
      } catch (error) {
        console.error('Failed to ensure program stage:', error);
        return false;
      }
  };

 
  const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setNotification(null);

      let newOrgUnitId = null;

      try {
        // Validate financial dates using computed values
        if (computedFinancialYearStart && computedFinancialYearEnd) {
          const normalizedStart = normalizeFinancialBoundary(computedFinancialYearStart);
          const normalizedEnd = normalizeFinancialBoundary(computedFinancialYearEnd);
          const start = parseFinancialBoundary(normalizedStart);
          let end = parseFinancialBoundary(normalizedEnd);
          if (!start || !end) {
            setNotification({ critical: true, message: 'Please enter valid month and day for financial year dates' });
            setSubmitting(false);
            return;
          }
          if (end <= start) {
            end = parseFinancialBoundary(normalizedEnd, 1);
          }
          if (!end || end <= start) {
            setNotification({ critical: true, message: 'Financial year end date must be after start date in the annual cycle' });
            setSubmitting(false);
            return;
          }
        }

        const requiredFields = {
          'Organization name': formData.organisationName,
          'Region': formData.regionId,
          'Organization type': formData.organisationType,
          'Organisation Email': formData.orgEmail
        };
        const missingFields = Object.entries(requiredFields).filter(([_, value]) => !(value || '').trim()).map(([name]) => name);
        if (missingFields.length > 0) throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        if (!currentUserData?.me?.id) throw new Error("Could not determine current user information");

        // 0. Upload KYC files
        let kycIds = [];
          if (kycFiles.length === 0) {
            setNotification({ critical: true, message: 'Please upload at least one KYC document' });
            setSubmitting(false);
            return;
          }
            try {
              setUploadingFiles(true);
              kycIds = await uploadKycFiles(kycFiles);
              console.log('✅ KYC file resource IDs:', kycIds);
            } catch (error) {
              setNotification({ critical: true, message: `KYC upload failed: ${error.message}` });
              setSubmitting(false);
              setUploadingFiles(false);
              return;
            } finally {
            setUploadingFiles(false);
        }

        // 1. Create main org unit using direct API
        const orgPayload = {
          name: formData.organisationName.trim(),
          shortName: formData.organisationName.trim().substring(0, 50),
          openingDate: new Date().toISOString().split('T')[0],
          parent: { id: formData.regionId },
          attributeValues: [
            { attribute: { id: ATTRIBUTE_IDS.ORG_TYPE }, value: formData.organisationType },
            formData.orgEmail && { attribute: { id: ATTRIBUTE_IDS.ORG_EMAIL }, value: formData.orgEmail.trim() }
          ].filter(Boolean)
        };

        console.log('Creating org unit with payload:', JSON.stringify(orgPayload, null, 2));
        const orgResponse = await createOrgUnitWithDuplicateHandling(engine, orgPayload);
        console.log('Org unit created, response:', orgResponse);
        newOrgUnitId = orgResponse.id || orgResponse.response?.uid;
        setCreatedOrgUnitId(newOrgUnitId);

        // Wait a bit for the org unit to be fully created
        await new Promise(resolve => setTimeout(resolve, 500));

        // 2. Create departments → sections → subSections
        for (const dept of formData.departments) {
          const deptPayload = {
            name: dept.name,
            shortName: dept.name.substring(0, 50),
            parent: { id: newOrgUnitId },
            openingDate: new Date().toISOString().split('T')[0]
          };
          const deptResp = await createOrgUnitWithDuplicateHandling(engine, deptPayload);
          const deptId = deptResp.id || deptResp.response?.uid;
          dept.createdId = deptId;

          for (const sec of dept.sections || []) {
            const secPayload = {
              name: sec.name,
              shortName: sec.name.substring(0, 50),
              parent: { id: deptId },
              openingDate: new Date().toISOString().split('T')[0]
            };
            const secResp = await createOrgUnitWithDuplicateHandling(engine, secPayload);
            const secId = secResp.id || secResp.response?.uid;
            sec.createdId = secId;

            for (const sub of sec.subSections || []) {
              const subPayload = {
                name: sub.name,
                shortName: sub.name.substring(0, 50),
                parent: { id: secId },
                openingDate: new Date().toISOString().split('T')[0]
              };
              const subResp = await createOrgUnitWithDuplicateHandling(engine, subPayload);
              sub.createdId = subResp.id || subResp.response?.uid;
            }
          }
        }

        // 3. Assign user to the main org unit only
        console.log('Assigning user to org unit:', newOrgUnitId, formData.organisationName);
        const mainOrgUnit = [{ id: newOrgUnitId, name: formData.organisationName }];
        await assignUserToSpecificOrgs(currentUserData.me.id, mainOrgUnit);
        console.log('User assigned successfully');

        // 4. Save company metadata (FY dates, org type, email) to program event
        console.log('Saving company metadata...');
        try {
          // ✅ FIX: Ensure we're using MM-DD format for storage
          let startPattern = computedFinancialYearStart;
          let endPattern = computedFinancialYearEnd;
          
          // Extract just MM-DD if it's a full date
          const startMatch = computedFinancialYearStart.match(/\d{4}-(\d{2}-\d{2})/);
          if (startMatch) {
            startPattern = startMatch[1];
          }
          const endMatch = computedFinancialYearEnd.match(/\d{4}-(\d{2}-\d{2})/);
          if (endMatch) {
            endPattern = endMatch[1];
          }

          await ensureMetadataProgramHasTrackedEntityType();
          await ensureMetadataProgramHasProgramStage();
          
          const storedFinancialDates = buildFinancialDatesForStorage(
              computedFinancialYearStart,
              computedFinancialYearEnd
            );

          await saveCompanyMetadataDirect(engine, newOrgUnitId, {
            orgType: formData.organisationType,
            email: formData.orgEmail,
            finStart: storedFinancialDates.start,
            finEnd: storedFinancialDates.end,
            kycFiles: JSON.stringify(kycIds)
          });

          console.log('Company metadata saved successfully');
        } catch (metadataError) {
          console.error('Failed to save metadata, continuing without it:', metadataError);
        }

        completeRegistration(true);

        try {
          await fetch('/api/notify-kyc', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              orgUnitId: newOrgUnitId,
              orgName: formData.organisationName,
              kycFileIds: kycIds,
              adminEmail: 'akakorozya@gmail.com'
            })
          });
        } catch (emailError) {
          console.warn('Failed to send KYC email:', emailError);
          // Don't block the flow
       }

        setNotification({ success: true, message: 'Organization created successfully! Redirecting...' });
        setTimeout(() => navigate('/dashboard'), 2000);

      } catch (error) {
        console.error("Submission failed:", error);
        setNotification({ critical: true, message: error.message });

        if (createdOrgUnitId) {
          try { await deleteOrgUnitDirect(createdOrgUnitId); } 
          catch (rollbackError) { console.error('Rollback failed:', rollbackError); }
        }
      } finally {
        setSubmitting(false);
      }
   };

  return (
    <>
      {!showForm ? (
        <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '60px', boxSizing: 'border-box' }}>
          <Button 
            primary 
            onClick={() => setShowForm(true)} 
            icon={<IconAdd16 />}
            data-test="create-org-button"
          >
            Create your Organization
          </Button>
        </div>
      ) : (
        <div style={{ padding: '24px', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#f8f8f8' }} data-test="org-form-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3>New Organization Unit</h3>
            <Button small secondary onClick={() => setShowForm(false)} icon={<IconFullscreenExit16 />}>Close</Button>
          </div>

          {notification && (
            <AlertBar critical={notification.critical} success={notification.success} onHidden={() => setNotification(null)}>
              {notification.message}
            </AlertBar>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label>Continent</label>
                <SingleSelect
                  selected={formData.continentId}
                  onChange={({ selected }) => setFormData(prev => ({ ...prev, continentId: selected, countryId: '', regionId: '' }))}
                  loading={orgUnitsLoading}
                  filterable
                >
                  {continent.map(cont => <SingleSelectOption key={cont.id} value={cont.id} label={cont.name} />)}
                </SingleSelect>
              </div>

              <div>
                <label>Country</label>
                <SingleSelect
                  selected={formData.countryId}
                  onChange={({ selected }) => setFormData(prev => ({ ...prev, countryId: selected, regionId: '' }))}
                  loading={orgUnitsLoading}
                  filterable
                >
                  {countries.map(c => <SingleSelectOption key={c.id} value={c.id} label={c.name} />)}
                </SingleSelect>
              </div>

              {formData.countryId && (
                <div>
                  <label>Region</label>
                  <SingleSelect
                    selected={formData.regionId}
                    onChange={({ selected }) => setFormData(prev => ({ ...prev, regionId: selected }))}
                    loading={orgUnitsLoading}
                    filterable
                  >
                    {regions.map(r => <SingleSelectOption key={r.id} value={r.id} label={r.name} />)}
                  </SingleSelect>
                </div>
              )}

              {formData.regionId && (
                <>
                  <div>
                    <label>Organization Name</label>
                    <InputField value={formData.organisationName} onChange={({ value }) => setFormData(prev => ({ ...prev, organisationName: value }))} required />
                  </div>

                  <div>
                    <label>Organization Email</label>
                    <InputField type="email" value={formData.orgEmail} onChange={({ value }) => setFormData(prev => ({ ...prev, orgEmail: value }))} validationText="Organization contact email" />
                  </div>

                  <div>
                    <label>Organization Type</label>
                    <InputField
                      value={formData.organisationType}
                      onChange={({ value }) => setFormData(prev => ({ ...prev, organisationType: value }))}
                      placeholder="Enter organization type"
                      required
                    />
                  </div>
                  {/* Financial Calendar Section */}
                <div style={{
                  gridColumn: '1 / -1',
                  marginBottom: '20px',
                  padding: '20px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9'
                  }}>
                  <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Financial/Business Calendar</h3>
                  <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                    Set your organization's financial year. This will be used for goal setting, reporting, and performance cycles.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label>Financial Year Start (Month - Day)</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <SingleSelect
                          selected={finStartMonth}
                          onChange={({ selected }) => setFinStartMonth(selected)}
                          width="100%"
                        >
                          {MONTH_OPTIONS.map(m => <SingleSelectOption key={m.value} value={m.value} label={m.label} />)}
                        </SingleSelect>
                        <InputField
                          value={finStartDay}
                          onChange={({ value }) => setFinStartDay(value.replace(/\D/g, '').slice(0, 2))}
                          placeholder="DD"
                          style={{ width: '60px' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label>Financial Year End (Month - Day)</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <SingleSelect
                          selected={finEndMonth}
                          onChange={({ selected }) => setFinEndMonth(selected)}
                          width="100%"
                        >
                          {MONTH_OPTIONS.map(m => <SingleSelectOption key={m.value} value={m.value} label={m.label} />)}
                        </SingleSelect>
                        <InputField
                          value={finEndDay}
                          onChange={({ value }) => setFinEndDay(value.replace(/\D/g, '').slice(0, 2))}
                          placeholder="DD"
                          style={{ width: '60px' }}
                        />
                      </div>
                    </div>
                  </div>
                  {financialDateErrors.dateRange && (
                    <AlertBar critical style={{ marginTop: '10px' }}>
                      {financialDateErrors.dateRange}
                    </AlertBar>
                  )}
                  {financialDateErrors.duration && (
                    <AlertBar warning style={{ marginTop: '10px' }}>
                      Note: {financialDateErrors.duration}
                    </AlertBar>
                  )}
                </div>
                </>
              )}

              {/* KYC Upload Section */}
              <div style={{
                gridColumn: '1 / -1',
                marginTop: '16px',
                padding: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}>
                <h4>KYC Documents (required)</h4>
                <p style={{ fontSize: '14px', color: '#666' }}>
                  Upload at least one document (PDF or image). These will be sent to the PMS team for verification.
                </p>
                <FileInput
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={({ files }) => setKycFiles(Array.from(files))}
                  placeholder="Choose KYC files"
                  disabled={uploadingFiles}
                />
                {kycFiles.length > 0 && (
                  <ul style={{ marginTop: '8px', fontSize: '14px' }}>
                    {kycFiles.map((file, idx) => (
                      <li key={idx}>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                        <button
                          type="button"
                          style={{ marginLeft: '8px', color: 'red', cursor: 'pointer' }}
                          onClick={() => setKycFiles(prev => prev.filter((_, i) => i !== idx))}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>   

            {formData.regionId && (
              <>
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <InputField value={formData.newDepartment} onChange={({ value }) => setFormData(prev => ({ ...prev, newDepartment: value }))} placeholder="Department name" fullWidth />
                    <Button small onClick={addDepartment} disabled={!formData.newDepartment.trim()}>Add Department</Button>
                  </div>
                </div>

                {formData.departments.length > 0 && (
                  <DepartmentManager
                    departments={formData.departments}
                    onRemoveDepartment={(deptId) => setFormData(prev => ({ ...prev, departments: prev.departments.filter(d => d.id !== deptId) }))}
                    onUpdateDepartment={(deptId, action, payload) => {
                      setFormData(prev => ({
                        ...prev,
                        departments: prev.departments.map(d => {
                          if (d.id !== deptId) return d;
                          switch(action) {
                            case 'addSection':
                              if(!d.newSection?.trim()) return d;
                              if (hasDuplicateSiblingName(d.sections || [], d.newSection)) {
                                alert(`A section named "${d.newSection.trim()}" already exists under "${d.name}".`);
                                return d;
                              }
                              return { ...d, sections: [...(d.sections || []), { id: Date.now(), name: d.newSection.trim(), newSubSection: '', subSections: [] }], newSection: '' };
                            case 'removeSection':
                              return { ...d, sections: (d.sections || []).filter(s => s.id !== payload) };
                            case 'newSection':
                              return { ...d, newSection: payload };
                            case 'updateSection':
                              const { sectionId, action: subAction, value } = payload;
                              return { ...d, sections: d.sections.map(s => {
                                if (s.id !== sectionId) return s;
                                switch(subAction) {
                                  case 'addSubSection':
                                    if(!s.newSubSection?.trim()) return s;
                                    if (hasDuplicateSiblingName(s.subSections || [], s.newSubSection)) {
                                      alert(`A subsection named "${s.newSubSection.trim()}" already exists under "${s.name}".`);
                                      return s;
                                    }
                                    return { ...s, subSections: [...(s.subSections || []), { id: Date.now(), name: s.newSubSection.trim() }], newSubSection: '' };
                                  case 'removeSubSection':
                                    return { ...s, subSections: (s.subSections || []).filter(ss => ss.id !== value) };
                                  case 'updateSubSectionInput':
                                    return { ...s, newSubSection: value };
                                  default: return s;
                                }
                              })};
                            default: return d;
                          }
                        })
                      }));
                    }}
                  />
                )}
              </>
            )}
            <div style={{ marginTop: '24px' }}>
              <Button primary type="submit" disabled={submitting || uploadingFiles}>
                {submitting || uploadingFiles ? <CircularLoader small /> : 'Create Organization'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default OrgUnitForm;
